import { BadRequestError, ConflictError, NotFoundError, UnauthorizedError, ForbiddenError, ExecutorBlockedError, LastAdminError } from "./gqty";

type ErrorMap = {
  BadRequestError?: BadRequestError;
  ConflictError?: ConflictError;
  UnauthorizedError?: UnauthorizedError;
  NotFoundError?: NotFoundError;
  ForbiddenError?: ForbiddenError;
  ExecutorBlockedError?: ExecutorBlockedError;
  LastAdminError?: LastAdminError;
};

interface WithOn {
  $on: Partial<ErrorMap>;
}

export function mapErrorsToMessages(errors: WithOn[] | undefined | null) {
  if (!errors) {
    return [];
  }

  return errors.flatMap((e) =>
    Object.values(e.$on)
      .filter((o): o is NonNullable<typeof o> => o != null)
      .flatMap((o) => {
        const parts: string[] = [];
        if (o.message) {
          parts.push(o.message);
        }
        if ('errors' in o && Array.isArray(o.errors)) {
          for (const kv of o.errors) {
            if (kv?.key && kv?.value) {
              parts.push(`${kv.key}: ${kv.value}`);
            }
          }
        }
        return parts;
      }),
  );
}

/** Read union error fragments so GQty includes them in the mutation response. */
export function touchMutationErrors(
  errors: Array<{ __typename?: string; $on?: Partial<ErrorMap> }> | null | undefined
) {
  for (const e of errors ?? []) {
    const typeName = e.__typename;
    const on = e.$on;
    if (!on) continue;

    switch (typeName) {
      case 'BadRequestError':
        void on.BadRequestError?.message;
        if (on.BadRequestError?.errors) {
          for (const kv of on.BadRequestError.errors) {
            void kv?.key;
            void kv?.value;
          }
        }
        break;
      case 'UnauthorizedError':
        void on.UnauthorizedError?.message;
        break;
      case 'ConflictError':
        void on.ConflictError?.message;
        break;
      case 'ForbiddenError':
        void on.ForbiddenError?.message;
        break;
      case 'NotFoundError':
        void on.NotFoundError?.message;
        break;
      case 'ExecutorBlockedError':
        void on.ExecutorBlockedError?.message;
        break;
      case 'LastAdminError':
        void on.LastAdminError?.message;
        break;
      default:
        void on.BadRequestError?.message;
        void on.UnauthorizedError?.message;
        void on.ConflictError?.message;
        void on.ForbiddenError?.message;
        void on.NotFoundError?.message;
        void on.ExecutorBlockedError?.message;
        void on.LastAdminError?.message;
        break;
    }
  }
}

const GENERIC_ERROR_MESSAGES = new Set(['Error', 'GraphQL Errors']);

function meaningfulGraphQlMessages(
  gqlErrors: Array<{ message?: string; extensions?: Record<string, unknown> }>
): string[] {
  const messages: string[] = [];
  for (const e of gqlErrors) {
    const msg = e.message?.trim();
    if (msg && !GENERIC_ERROR_MESSAGES.has(msg)) {
      messages.push(msg);
    }
    const ext = e.extensions;
    if (!ext) continue;
    const code = ext['code'];
    if (typeof code === 'string' && code.trim() && !GENERIC_ERROR_MESSAGES.has(code)) {
      messages.push(`(${code})`);
    }
    for (const key of ['detail', 'details', 'reason']) {
      const value = ext[key];
      if (typeof value === 'string' && value.trim()) {
        messages.push(value.trim());
      }
    }
  }
  return messages;
}

export function formatApiError(error: unknown): string {
  const payloadErrors = (error as { errors?: WithOn[] } | null)?.errors;
  if (payloadErrors?.length) {
    touchMutationErrors(payloadErrors);
    const messages = mapErrorsToMessages(payloadErrors);
    if (messages.length) return messages.join('; ');
  }

  const cause = (error as { cause?: unknown })?.cause ?? error;
  const gqlErrors = (
    cause as {
      graphQLErrors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
    }
  )?.graphQLErrors;
  if (gqlErrors?.length) {
    const messages = meaningfulGraphQlMessages(gqlErrors);
    if (messages.length) return [...new Set(messages)].join('; ');
  }

  const directGql = (error as { graphQLErrors?: typeof gqlErrors })?.graphQLErrors;
  if (directGql?.length) {
    const messages = meaningfulGraphQlMessages(directGql);
    if (messages.length) return [...new Set(messages)].join('; ');
  }

  if (error instanceof Error && error.message && !GENERIC_ERROR_MESSAGES.has(error.message)) {
    return error.message;
  }

  return 'An error occurred. Check Admin permissions and try again.';
}
